import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { RotateCcw } from 'lucide-react'

export default function ReopenStepModal({
  open,
  setOpen,
  stepName = '',
  isAdminStep = false,
  reason,
  setReason,
  onConfirm,
  loading = false,
}) {
  const handleClose = () => {
    if (!loading) setOpen(false);
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-90"
          leave="ease-in duration-100"
          leaveFrom="opacity-90"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                    <RotateCcw className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Reopen Step
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {isAdminStep ? (
                        <>This will reopen <span className="font-medium text-gray-700 dark:text-gray-300">{stepName}</span>. This is an internal step, so the customer won't be notified.</>
                      ) : (
                        <>This will reopen <span className="font-medium text-gray-700 dark:text-gray-300">{stepName}</span> and notify the customer that further action is needed.</>
                      )}
                    </p>
                  </div>
                </div>

                {!isAdminStep && (
                  <div className="mt-4">
                    <label htmlFor="reopen_reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reason (sent to customer)
                    </label>
                    <textarea
                      id="reopen_reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Explain why this step needs to be revisited..."
                      rows={3}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                    />
                  </div>
                )}

                <div className="mt-5 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={loading}
                    className="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto disabled:opacity-50"
                    onClick={onConfirm}
                  >
                    {loading ? 'Reopening...' : 'Reopen Step'}
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600 sm:mt-0 sm:w-auto"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
